import type { CappsOptInRenterRequestPaymentApplicationApiRequestDto } from '@zg-rentals/ts-capps-client';
import {
  CappsRentPaymentWebApiHandler_optInRenterRequestPaymentApplication,
  CappsRentPaymentWebApiHandler_optOutRenterRequestPaymentApplication,
  RenterRequestPaymentStatus,
} from '@zg-rentals/ts-capps-client';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useCappsRentPaymentWebApiHandler_getRenterRequestPaymentApplication } from '@zg-rentals/ts-capps-client-react-query';
import useGetUser from '../hooks/useGetUser';

export function useOptInOutRenterRequestPayment(
  applicationId: string | undefined,
  listingAlias: string | null | undefined,
) {
  const { data: userData } = useGetUser();
  const queryClient = useQueryClient();

  return async (value: boolean) => {
    if (!listingAlias || !applicationId) {
      throw new Error('listingAlias is required for optInOutRenterRequestPayment');
    }
    const postData: CappsOptInRenterRequestPaymentApplicationApiRequestDto = {
      applicationId,
      listingAlias: listingAlias!,
    };

    if (value) {
      await CappsRentPaymentWebApiHandler_optInRenterRequestPaymentApplication(postData);
    } else {
      await CappsRentPaymentWebApiHandler_optOutRenterRequestPaymentApplication(postData);
    }
    queryClient.invalidateQueries({
      queryKey: useCappsRentPaymentWebApiHandler_getRenterRequestPaymentApplication.getKey({
        applicationId,
      }),
    });
  };
}

/**
 * Returns the renter's opt-in status for payments
 * @param applicationId The application id, undefined disables the query
 * @returns true/false if the user opted in or out of payments, null if the user has not yet requested, undefined if loading
 */
export function useRenterRequestedZillowPayments(
  applicationId?: string,
  options?: {
    enabled?: boolean;
  },
) {
  const { data, isLoading } = useCappsRentPaymentWebApiHandler_getRenterRequestPaymentApplication(
    {
      applicationId: applicationId!,
    },
    {
      ...options,
      enabled: (options?.enabled ?? true) && applicationId != null,
    },
  );

  // Undefined means we're still loading
  if (isLoading) return undefined;

  if (!data?.status) {
    return null;
  }

  return (
    data.status === RenterRequestPaymentStatus.RENTER_OPTED_IN ||
    // Landlord status implies that the renter opted in
    data.status === RenterRequestPaymentStatus.LANDLORD_ACCEPTED ||
    data.status === RenterRequestPaymentStatus.LANDLORD_REJECTED
  );
}
